import { useAppUser } from '../admin/AppUserContext';
import { CompanyAdviserBillingPanel } from './advisers/AdviserBillingPanel';
import ClientAdviserAccessPanel from './advisers/ClientAdviserAccessPanel';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Building,
  Users,
  Settings,
  Save,
  Edit,
  Trash2,
  Crown,
  Eye,
  Shield,
  Mail,
  Clock,
  AlertTriangle,
  CheckCircle,
  X,
  AlertCircle,
  Upload,
  Image,
  UserPlus,
  CreditCard,
  ExternalLink,
} from 'lucide-react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import { useOrganizationList } from '@clerk/clerk-react';
import Header from '../components/Header';
import './CompanyWorkPages.css';
import {
  companiesApi,
  Company,
  TeamMember,
  UpdateCompanyData,
  CompanyInvitation,
  InviteUserData,
} from '../services/api';

// Notification types
type NotificationType = 'success' | 'error' | 'info' | 'warning';

interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message?: string;
  duration?: number;
}

export default function CompanyAdminPage() {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [company, setCompany] = useState<Company | null>(null);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [invitations, setInvitations] = useState<CompanyInvitation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMembers, setIsLoadingMembers] = useState(true);
  const [_isLoadingInvitations, setIsLoadingInvitations] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Tab state
  const [activeTab, setActiveTab] = useState<'general' | 'team' | 'billing'>(() => {
    const tab = searchParams.get('tab');
    return tab === 'general' || tab === 'team' || tab === 'billing' ? tab : 'general';
  });

  // Company editing state
  const [isEditingCompany, setIsEditingCompany] = useState(false);
  const [companyForm, setCompanyForm] = useState<UpdateCompanyData>({});
  const [isSavingCompany, setIsSavingCompany] = useState(false);

  // Logo upload state
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Member management state
  const [processingMemberId, setProcessingMemberId] = useState<string | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);

  // Invite modal state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState<InviteUserData>({ email: '', role: 'member' });
  const [isInviting, setIsInviting] = useState(false);

  // Delete company modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [isDeletingCompany, setIsDeletingCompany] = useState(false);

  // Notification state
  const [notifications, setNotifications] = useState<Notification[]>([]);

  // Sync activeTab from URL when URL changes
  const tabFromUrl = searchParams.get('tab');
  useEffect(() => {
    if (tabFromUrl === 'general' || tabFromUrl === 'team' || tabFromUrl === 'billing') {
      setActiveTab(tabFromUrl);
    }
  }, [tabFromUrl]);

  // Update URL when tab changes (user clicked a tab)
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set('tab', activeTab);
        return newParams;
      },
      { replace: true }
    );
  }, [activeTab, setSearchParams]);

  const { isSupportSession } = useAppUser();
  // Resolve Clerk organization for this company (for logo upload)
  const { isLoaded: isOrgListLoaded, userMemberships } = useOrganizationList({
    userMemberships: { infinite: true },
  });
  const providerOrganization =
    company?.clerkOrgId && userMemberships?.data
      ? userMemberships.data.find(
          (m: { organization: { id: string } }) => m.organization.id === company.clerkOrgId
        )?.organization
      : undefined;
  const currentOrganization = isSupportSession ? undefined : providerOrganization;

  // Notification management
  const showNotification = (
    type: NotificationType,
    title: string,
    message?: string,
    duration = 5000
  ) => {
    const id = Math.random().toString(36).substr(2, 9);
    const notification: Notification = { id, type, title, message, duration };

    setNotifications((prev) => [...prev, notification]);

    // Auto-remove after duration
    setTimeout(() => {
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    }, duration);
  };

  const removeNotification = (id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  // Fetch company data
  useEffect(() => {
    const fetchData = async () => {
      if (!companyId) {
        setError('Company ID is required');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        // Fetch company details
        const companyResponse = await companiesApi.get(companyId);

        if (companyResponse.success && companyResponse.data) {
          setCompany(companyResponse.data);
          setCompanyForm({
            name: companyResponse.data.name,
            size: companyResponse.data.size,
            industry: companyResponse.data.industry,
            description: companyResponse.data.description,
            logo: companyResponse.data.logo,
          });

          // Check if user is admin or owner
          if (companyResponse.data.role !== 'admin' && companyResponse.data.role !== 'owner') {
            setError('Access denied - admin or owner role required');
            setIsLoading(false);
            return;
          }
        } else {
          setError(companyResponse.error || 'Failed to fetch company');
        }

        // Fetch team members
        setIsLoadingMembers(true);
        const membersResponse = await companiesApi.getTeamMembers(companyId);

        if (membersResponse.success && membersResponse.data) {
          setTeamMembers(membersResponse.data);
        } else {
          console.error('Failed to fetch team members:', membersResponse.error);
        }

        // Fetch invitations
        setIsLoadingInvitations(true);
        const invitationsResponse = await companiesApi.getCompanyInvitations(companyId);

        if (invitationsResponse.success && invitationsResponse.data) {
          setInvitations(invitationsResponse.data);
        } else {
          console.error('Failed to fetch invitations:', invitationsResponse.error);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An unexpected error occurred');
      } finally {
        setIsLoading(false);
        setIsLoadingMembers(false);
        setIsLoadingInvitations(false);
      }
    };

    fetchData();
  }, [companyId]);

  const handleSaveCompany = async () => {
    if (!companyId) return;

    setIsSavingCompany(true);
    try {
      const response = await companiesApi.updateCompany(companyId, companyForm);

      if (response.success && response.data) {
        setCompany(response.data);
        setIsEditingCompany(false);
        showNotification(
          'success',
          'Company Updated',
          'Company details have been successfully updated.'
        );
      } else {
        showNotification(
          'error',
          'Update Failed',
          response.error || 'Failed to update company details.'
        );
      }
    } catch (err) {
      console.error('Error updating company:', err);
      showNotification(
        'error',
        'Update Failed',
        'An unexpected error occurred while updating the company.'
      );
    } finally {
      setIsSavingCompany(false);
    }
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      console.log('No file selected');
      return;
    }

    console.log('File selected:', file.name, file.size, file.type);
    console.log('Organization object:', currentOrganization);
    console.log('Organization ID:', currentOrganization?.id);
    console.log('Organization name:', currentOrganization?.name);

    if (!currentOrganization) {
      console.error('Organization not available from useOrganizationList hook');
      showNotification(
        'error',
        'Organization Error',
        'Unable to access organization. Please refresh the page and try again.'
      );
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      showNotification('error', 'Invalid File', 'Please select an image file.');
      return;
    }

    // Validate file size (10MB limit as per Clerk documentation)
    if (file.size > 10 * 1024 * 1024) {
      showNotification('error', 'File Too Large', 'Image size must be less than 10MB.');
      return;
    }

    setIsUploadingLogo(true);
    console.log('Starting logo upload to Clerk...');

    try {
      // Upload logo to Clerk organization
      console.log('Calling organization.setLogo with file:', file);
      const updatedOrg = await currentOrganization.setLogo({ file });
      console.log('Logo upload response from Clerk:', updatedOrg);
      console.log('New image URL:', updatedOrg?.imageUrl);

      // Update our database with the new logo URL
      if (updatedOrg?.imageUrl && companyId) {
        console.log('Updating database with logo URL:', updatedOrg.imageUrl);
        const response = await companiesApi.updateCompany(companyId, { logo: updatedOrg.imageUrl });
        console.log('Database update response:', response);

        if (response.success && response.data) {
          setCompany(response.data);
          setCompanyForm((prev) => ({ ...prev, logo: updatedOrg.imageUrl }));
          showNotification(
            'success',
            'Logo Updated',
            'Company logo has been successfully updated.'
          );
        } else {
          showNotification(
            'error',
            'Database Update Failed',
            response.error || 'Logo uploaded to Clerk but failed to update database.'
          );
        }
      } else {
        console.error('No imageUrl in response or no companyId');
        showNotification(
          'error',
          'Upload Issue',
          'Logo uploaded but no image URL returned or company ID missing.'
        );
      }
    } catch (err) {
      console.error('Error uploading logo:', err);
      showNotification(
        'error',
        'Upload Failed',
        `Failed to upload logo: ${err instanceof Error ? err.message : 'Unknown error'}`
      );
    } finally {
      setIsUploadingLogo(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveLogo = async () => {
    if (!currentOrganization || !companyId) return;

    const confirmed = confirm('Are you sure you want to remove the company logo?');
    if (!confirmed) return;

    setIsUploadingLogo(true);
    try {
      // Remove logo from Clerk organization
      await currentOrganization.setLogo({ file: null });

      // Update our database
      const response = await companiesApi.updateCompany(companyId, { logo: null });

      if (response.success && response.data) {
        setCompany(response.data);
        setCompanyForm((prev) => ({ ...prev, logo: null }));
        showNotification('success', 'Logo Removed', 'Company logo has been successfully removed.');
      } else {
        showNotification(
          'error',
          'Database Update Failed',
          'Logo removed from Clerk but failed to update database.'
        );
      }
    } catch (err) {
      console.error('Error removing logo:', err);
      showNotification('error', 'Remove Failed', 'Failed to remove logo. Please try again.');
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const handleUpdateRole = async (
    membershipId: string,
    newRole: 'owner' | 'admin' | 'member' | 'viewer'
  ) => {
    if (!companyId) return;

    // Special handling for owner role changes
    if (newRole === 'owner' && company?.role === 'owner') {
      const confirmed = confirm(
        '⚠️ Ownership Transfer\n\n' +
          'You are about to transfer ownership of this company to another user. ' +
          'As the new owner, they will have full control including billing and the ability to remove you as an admin.\n\n' +
          'Are you sure you want to proceed?'
      );

      if (!confirmed) {
        return;
      }
    }

    setProcessingMemberId(membershipId);
    try {
      const response = await companiesApi.updateMemberRole(companyId, membershipId, {
        role: newRole,
      });

      if (response.success) {
        // Update local state using membershipId to find the member
        setTeamMembers((prev) =>
          prev.map((member) =>
            member.membershipId === membershipId ? { ...member, role: newRole } : member
          )
        );

        if (newRole === 'owner' && company?.role === 'owner') {
          showNotification(
            'success',
            'Ownership Transferred',
            'Ownership has been successfully transferred! The new owner now has full control of the company.'
          );
          // Refresh the page to update the current user's role
          setTimeout(() => window.location.reload(), 2000);
        } else {
          showNotification(
            'success',
            'Role Updated',
            `Role has been successfully updated to ${newRole}.`
          );
        }
      } else {
        showNotification(
          'error',
          'Update Failed',
          response.error || 'Failed to update member role.'
        );
      }
    } catch (err) {
      console.error('Error updating role:', err);
      showNotification(
        'error',
        'Update Failed',
        'An unexpected error occurred while updating the role.'
      );
    } finally {
      setProcessingMemberId(null);
    }
  };

  const handleRemoveMember = async (membershipId: string, memberName: string) => {
    if (!companyId) return;

    if (!confirm(`Are you sure you want to remove ${memberName} from the company?`)) {
      return;
    }

    setProcessingMemberId(membershipId);
    try {
      const response = await companiesApi.removeMember(companyId, membershipId);

      if (response.success) {
        // Remove from local state using membershipId
        setTeamMembers((prev) => prev.filter((member) => member.membershipId !== membershipId));
        showNotification(
          'success',
          'Member Removed',
          `${memberName} has been successfully removed from the company.`
        );
      } else {
        showNotification(
          'error',
          'Removal Failed',
          response.error || 'Failed to remove member from the company.'
        );
      }
    } catch (err) {
      console.error('Error removing member:', err);
      showNotification(
        'error',
        'Removal Failed',
        'An unexpected error occurred while removing the member.'
      );
    } finally {
      setProcessingMemberId(null);
    }
  };

  const handleUpdateInvitationStatus = async (
    invitationId: string,
    newStatus: 'pending' | 'accepted' | 'expired' | 'revoked'
  ) => {
    if (!companyId) return;

    setProcessingMemberId(invitationId);
    try {
      const response = await companiesApi.updateInvitationStatus(companyId, invitationId, {
        status: newStatus,
      });

      if (response.success) {
        // Update local state
        setInvitations((prev) =>
          prev.map((invitation) =>
            invitation.id === invitationId
              ? { ...invitation, status: newStatus, updatedAt: new Date().toISOString() }
              : invitation
          )
        );
        showNotification(
          'success',
          'Status Updated',
          `Invitation status has been updated to ${newStatus}.`
        );
      } else {
        showNotification(
          'error',
          'Update Failed',
          response.error || 'Failed to update invitation status.'
        );
      }
    } catch (err) {
      console.error('Error updating invitation status:', err);
      showNotification(
        'error',
        'Update Failed',
        'An unexpected error occurred while updating the invitation status.'
      );
    } finally {
      setProcessingMemberId(null);
    }
  };

  const handleSyncInvitations = async () => {
    if (!companyId) return;

    setIsSyncing(true);
    try {
      const response = await companiesApi.syncInvitations(companyId);

      if (response.success && response.data) {
        const { resolved, revoked, errors, details } = response.data;

        // Refresh invitations list
        const invitationsResponse = await companiesApi.getCompanyInvitations(companyId);
        if (invitationsResponse.success && invitationsResponse.data) {
          setInvitations(invitationsResponse.data);
        }

        // Show detailed results
        showNotification(
          'success',
          'Sync Completed',
          `✅ ${resolved} invitations resolved • 🗑️ ${revoked} cleaned up • ❌ ${errors} errors`,
          8000
        );

        // Show details if there are any
        if (details.length > 0) {
          details.slice(0, 3).forEach((detail: string, index: number) => {
            setTimeout(
              () => {
                showNotification('info', 'Sync Detail', detail, 4000);
              },
              (index + 1) * 1000
            );
          });
        }
      } else {
        showNotification(
          'error',
          'Sync Failed',
          response.error || 'Failed to sync invitations with Clerk.'
        );
      }
    } catch (err) {
      console.error('Error syncing invitations:', err);
      showNotification(
        'error',
        'Sync Failed',
        'An unexpected error occurred during synchronization.'
      );
    } finally {
      setIsSyncing(false);
    }
  };

  const _getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'accepted':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'expired':
        return 'bg-red-100 text-red-800';
      case 'revoked':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'owner':
        return <Crown className="h-4 w-4 text-purple-600" />;
      case 'admin':
        return <Crown className="h-4 w-4 text-yellow-500" />;
      case 'member':
        return <Shield className="h-4 w-4 text-blue-500" />;
      case 'viewer':
        return <Eye className="h-4 w-4 text-gray-500" />;
      default:
        return <Shield className="h-4 w-4 text-gray-500" />;
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'owner':
        return 'bg-purple-100 text-purple-800';
      case 'admin':
        return 'bg-yellow-100 text-yellow-800';
      case 'member':
        return 'bg-blue-100 text-blue-800';
      case 'viewer':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleInviteUser = async () => {
    if (!companyId || !inviteForm.email.trim()) {
      showNotification('error', 'Invalid Input', 'Please enter a valid email address.');
      return;
    }

    setIsInviting(true);
    try {
      const response = await companiesApi.inviteUser(companyId, inviteForm);

      if (response.success && response.data) {
        showNotification(
          'success',
          'Invitation Sent',
          `Invitation has been sent to ${response.data.email}`
        );
        setShowInviteModal(false);
        setInviteForm({ email: '', role: 'member' });

        // Refresh the invitations list
        const invitationsResponse = await companiesApi.getCompanyInvitations(companyId);
        if (invitationsResponse.success && invitationsResponse.data) {
          setInvitations(invitationsResponse.data);
        }

        // Refresh team members list in case the user was added directly
        const membersResponse = await companiesApi.getTeamMembers(companyId);
        if (membersResponse.success && membersResponse.data) {
          setTeamMembers(membersResponse.data);
        }
      } else {
        showNotification(
          'error',
          'Invitation Failed',
          response.error || 'Failed to send invitation.'
        );
      }
    } catch (err) {
      console.error('Error inviting user:', err);
      showNotification(
        'error',
        'Invitation Failed',
        'An unexpected error occurred while sending the invitation.'
      );
    } finally {
      setIsInviting(false);
    }
  };

  const handleDeleteCompany = async () => {
    if (!companyId || !company) return;

    if (deleteConfirmation !== company.name) {
      showNotification(
        'error',
        'Confirmation Error',
        `Please type "${company.name}" exactly to confirm deletion.`
      );
      return;
    }

    setIsDeletingCompany(true);
    try {
      const response = await companiesApi.deleteCompany(companyId);

      if (response.success) {
        showNotification(
          'success',
          'Company Deleted',
          'Company has been successfully deleted. Redirecting to dashboard...'
        );
        setTimeout(() => navigate('/dashboard'), 2000);
      } else {
        showNotification('error', 'Delete Failed', response.error || 'Failed to delete company.');
      }
    } catch (err) {
      console.error('Error deleting company:', err);
      showNotification(
        'error',
        'Delete Failed',
        'An unexpected error occurred while deleting the company.'
      );
    } finally {
      setIsDeletingCompany(false);
    }
  };

  // Notification Component
  const NotificationContainer = () => (
    <div className="fixed top-4 right-4 z-50 space-y-3 max-w-md">
      <AnimatePresence>
        {notifications.map((notification) => (
          <motion.div
            key={notification.id}
            initial={{ opacity: 0, x: 300, scale: 0.3 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 300, scale: 0.5, transition: { duration: 0.2 } }}
            className={`w-full min-w-80 shadow-lg rounded-lg pointer-events-auto overflow-hidden ${
              notification.type === 'success'
                ? 'bg-green-50 border border-green-200'
                : notification.type === 'error'
                  ? 'bg-red-50 border border-red-200'
                  : notification.type === 'warning'
                    ? 'bg-yellow-50 border border-yellow-200'
                    : 'bg-blue-50 border border-blue-200'
            }`}
          >
            <div className="p-5">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  {notification.type === 'success' && (
                    <CheckCircle className="h-6 w-6 text-green-400" />
                  )}
                  {notification.type === 'error' && (
                    <AlertCircle className="h-6 w-6 text-red-400" />
                  )}
                  {notification.type === 'warning' && (
                    <AlertTriangle className="h-6 w-6 text-yellow-400" />
                  )}
                  {notification.type === 'info' && <Settings className="h-6 w-6 text-blue-400" />}
                </div>
                <div className="ml-4 flex-1 pt-0.5">
                  <p
                    className={`text-sm font-medium ${
                      notification.type === 'success'
                        ? 'text-green-900'
                        : notification.type === 'error'
                          ? 'text-red-900'
                          : notification.type === 'warning'
                            ? 'text-yellow-900'
                            : 'text-blue-900'
                    }`}
                  >
                    {notification.title}
                  </p>
                  {notification.message && (
                    <p
                      className={`mt-1 text-sm ${
                        notification.type === 'success'
                          ? 'text-green-700'
                          : notification.type === 'error'
                            ? 'text-red-700'
                            : notification.type === 'warning'
                              ? 'text-yellow-700'
                              : 'text-blue-700'
                      }`}
                    >
                      {notification.message}
                    </p>
                  )}
                </div>
                <div className="ml-4 flex-shrink-0 flex">
                  <button
                    className={`rounded-md inline-flex ${
                      notification.type === 'success'
                        ? 'text-green-400 hover:text-green-500'
                        : notification.type === 'error'
                          ? 'text-red-400 hover:text-red-500'
                          : notification.type === 'warning'
                            ? 'text-yellow-400 hover:text-yellow-500'
                            : 'text-blue-400 hover:text-blue-500'
                    } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`}
                    onClick={() => removeNotification(notification.id)}
                  >
                    <span className="sr-only">Close</span>
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );

  if (isLoading) {
    return (
      <div className="company-work-page company-settings-page">
        <Header title="Loading..." />
        <main className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-strategy-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-gray-600">Loading admin panel...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error || !company) {
    return (
      <div className="company-work-page company-settings-page">
        <Header title="Error" />
        <main className="container mx-auto px-4 py-8">
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <div className="h-16 w-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="h-8 w-8 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/dashboard')}
              className="bg-strategy-600 text-white px-6 py-3 rounded-lg hover:bg-strategy-700 transition-colors"
            >
              Back to Dashboard
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="company-work-page company-settings-page">
      <NotificationContainer />

      {/* Delete Company Modal */}
      <AnimatePresence>
        {showDeleteModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowDeleteModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-red-600 flex items-center">
                  <AlertTriangle className="h-5 w-5 mr-2" />
                  Delete Company
                </h3>
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeleteConfirmation('');
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4 mb-6">
                <div className="bg-red-50 border-l-4 border-red-400 p-4">
                  <div className="flex items-center">
                    <AlertTriangle className="h-5 w-5 text-red-400 mr-2" />
                    <p className="text-red-800 font-medium">This action cannot be undone!</p>
                  </div>
                </div>

                <div className="text-sm text-gray-700 space-y-2">
                  <p>
                    <strong>Deleting this company will permanently remove:</strong>
                  </p>
                  <ul className="list-disc list-inside ml-4 space-y-1">
                    <li>All company data and settings</li>
                    <li>All team members and their access</li>
                    <li>All strategies, objectives, and projects</li>
                    <li>All tasks and organizational structure</li>
                    <li>All invitations and pending memberships</li>
                  </ul>
                </div>

                <div className="border-t pt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    To confirm, type "{company?.name}" below:
                  </label>
                  <input
                    type="text"
                    value={deleteConfirmation}
                    onChange={(e) => setDeleteConfirmation(e.target.value)}
                    placeholder={`Type "${company?.name}" to confirm`}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    disabled={isDeletingCompany}
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeleteConfirmation('');
                  }}
                  disabled={isDeletingCompany}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteCompany}
                  disabled={isDeletingCompany || deleteConfirmation !== company?.name}
                  className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
                >
                  {isDeletingCompany ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Deleting...</span>
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4" />
                      <span>Delete Company</span>
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Invite Member Modal */}
      <AnimatePresence>
        {showInviteModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowInviteModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <UserPlus className="h-5 w-5 mr-2 text-strategy-600" />
                  Invite Team Member
                </h3>
                <button
                  onClick={() => setShowInviteModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={inviteForm.email}
                    onChange={(e) => setInviteForm((prev) => ({ ...prev, email: e.target.value }))}
                    placeholder="Enter email address"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-strategy-500 focus:border-transparent"
                    disabled={isInviting}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
                  <select
                    value={inviteForm.role}
                    onChange={(e) => setInviteForm((prev) => ({ ...prev, role: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-strategy-500 focus:border-transparent"
                    disabled={isInviting}
                  >
                    <option value="viewer">Viewer</option>
                    <option value="member">Member</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>

                <div className="text-sm text-gray-500">
                  <p>
                    <strong>Viewer:</strong> Can view company data but cannot make changes
                  </p>
                  <p>
                    <strong>Member:</strong> Can participate in projects and tasks
                  </p>
                  <p>
                    <strong>Admin:</strong> Can manage team members and company settings
                  </p>
                  <p className="text-xs text-gray-400 mt-2">
                    <strong>Note:</strong> Owner permissions cannot be assigned through invitations
                  </p>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowInviteModal(false)}
                  disabled={isInviting}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleInviteUser}
                  disabled={isInviting || !inviteForm.email.trim()}
                  className="bg-strategy-600 text-white px-4 py-2 rounded-lg hover:bg-strategy-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
                >
                  {isInviting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <Mail className="h-4 w-4" />
                      <span>Send Invitation</span>
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <Header
        title="Company settings"
        subtitle={
          activeTab === 'general'
            ? 'Keep your company profile and planning context up to date.'
            : activeTab === 'team'
              ? 'Give your team the right access to move the plan forward.'
              : 'Manage products and authorised payment cards for this company.'
        }
        company={{
          name: company.name,
          logo: company.logo || currentOrganization?.imageUrl,
        }}
      />

      <main className="company-work-content company-work-settings">
        {/* Tab Navigation */}
        <div className="company-work-tabs">
          <nav
            className="flex overflow-x-auto scrollbar-none"
            aria-label="Company settings sections"
          >
            <button
              type="button"
              aria-current={activeTab === 'general' ? 'page' : undefined}
              onClick={() => setActiveTab('general')}
              className={`flex-1 min-w-0 py-3 px-3 sm:py-4 sm:px-4 md:px-6 font-medium text-xs sm:text-sm transition-colors flex items-center justify-center gap-1.5 sm:space-x-2 border-b-2 shrink-0 ${
                activeTab === 'general'
                  ? 'border-blue-600 text-blue-600 bg-blue-50/50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Building className="h-4 w-4 flex-shrink-0" />
              <span>Company profile</span>
            </button>
            <button
              type="button"
              aria-current={activeTab === 'team' ? 'page' : undefined}
              onClick={() => setActiveTab('team')}
              className={`flex-1 min-w-0 py-3 px-3 sm:py-4 sm:px-4 md:px-6 font-medium text-xs sm:text-sm transition-colors flex items-center justify-center gap-1.5 sm:space-x-2 border-b-2 shrink-0 ${
                activeTab === 'team'
                  ? 'border-blue-600 text-blue-600 bg-blue-50/50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Users className="h-4 w-4 flex-shrink-0" />
              <span>Team & access</span>
            </button>
            <button
              type="button"
              aria-current={activeTab === 'billing' ? 'page' : undefined}
              onClick={() => setActiveTab('billing')}
              className={`flex-1 min-w-0 py-3 px-3 sm:py-4 sm:px-4 md:px-6 font-medium text-xs sm:text-sm transition-colors flex items-center justify-center gap-1.5 sm:space-x-2 border-b-2 shrink-0 ${
                activeTab === 'billing'
                  ? 'border-blue-600 text-blue-600 bg-blue-50/50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <CreditCard className="h-4 w-4 flex-shrink-0" />
              <span>Billing</span>
            </button>
          </nav>
        </div>

        {company && (
          <>
            <ClientAdviserAccessPanel companyId={company.id} />
          </>
        )}

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          {activeTab === 'billing' && (
            <motion.section
              key="billing"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              aria-label="Company billing"
              className="space-y-6"
            >
              <CompanyAdviserBillingPanel companyId={company.id} />
              <div className="adviser-panel">
                <h2>Personal subscriptions</h2>
                <p>Your personal Founder subscription is managed separately from this company.</p>
                <Link to="/billing" className="adviser-text-link">
                  Manage personal subscriptions <ExternalLink size={14} aria-hidden="true" />
                </Link>
              </div>
            </motion.section>
          )}
          {activeTab === 'general' && (
            <motion.div
              key="general"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-6"
            >
              {/* Company Settings */}
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                    <Building className="h-6 w-6 mr-2 text-strategy-600" />
                    Company profile
                  </h2>
                  <button
                    onClick={() => setIsEditingCompany(!isEditingCompany)}
                    className="text-strategy-600 hover:text-strategy-700 flex items-center space-x-1"
                  >
                    <Edit className="h-4 w-4" />
                    <span>{isEditingCompany ? 'Cancel editing' : 'Edit profile'}</span>
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Company Name
                    </label>
                    {isEditingCompany ? (
                      <input
                        type="text"
                        value={companyForm.name || ''}
                        onChange={(e) =>
                          setCompanyForm((prev) => ({ ...prev, name: e.target.value }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-strategy-500 focus:border-transparent"
                      />
                    ) : (
                      <p className="text-gray-900 py-2">{company.name}</p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Company Size
                      </label>
                      {isEditingCompany ? (
                        <select
                          value={companyForm.size || ''}
                          onChange={(e) =>
                            setCompanyForm((prev) => ({ ...prev, size: e.target.value }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-strategy-500 focus:border-transparent"
                        >
                          <option value="startup">Startup</option>
                          <option value="small">Small</option>
                          <option value="medium">Medium</option>
                          <option value="large">Large</option>
                          <option value="enterprise">Enterprise</option>
                        </select>
                      ) : (
                        <p className="text-gray-900 py-2 capitalize">{company.size}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Industry
                      </label>
                      {isEditingCompany ? (
                        <input
                          type="text"
                          value={companyForm.industry || ''}
                          onChange={(e) =>
                            setCompanyForm((prev) => ({ ...prev, industry: e.target.value }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-strategy-500 focus:border-transparent"
                        />
                      ) : (
                        <p className="text-gray-900 py-2 capitalize">{company.industry}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    {isEditingCompany ? (
                      <textarea
                        value={companyForm.description || ''}
                        onChange={(e) =>
                          setCompanyForm((prev) => ({ ...prev, description: e.target.value }))
                        }
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-strategy-500 focus:border-transparent"
                      />
                    ) : (
                      <p className="text-gray-900 py-2">
                        {company.description || 'No description provided'}
                      </p>
                    )}
                  </div>

                  {/* Logo Upload Section */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Company Logo
                    </label>
                    <div className="flex items-center space-x-4">
                      {/* Current Logo Display */}
                      <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
                        {company.logo || currentOrganization?.imageUrl ? (
                          <img
                            src={company.logo || currentOrganization?.imageUrl}
                            alt="Company Logo"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <Image className="h-8 w-8 text-gray-400" />
                        )}
                      </div>

                      {/* Upload Controls */}
                      <div className="flex flex-col space-y-2">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => fileInputRef.current?.click()}
                            disabled={
                              isUploadingLogo ||
                              (!isOrgListLoaded && !!company?.clerkOrgId) ||
                              (isOrgListLoaded && !currentOrganization)
                            }
                            className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex items-center space-x-2"
                            title={
                              isOrgListLoaded && !currentOrganization && company?.clerkOrgId
                                ? 'Organization not available for this company'
                                : undefined
                            }
                          >
                            {isUploadingLogo ? (
                              <>
                                <div className="w-4 h-4 border-2 border-gray-600 border-t-transparent rounded-full animate-spin" />
                                <span>Uploading...</span>
                              </>
                            ) : (
                              <>
                                <Upload className="h-4 w-4" />
                                <span>
                                  {!isOrgListLoaded && company?.clerkOrgId
                                    ? 'Loading...'
                                    : isOrgListLoaded && !currentOrganization && company?.clerkOrgId
                                      ? 'Org unavailable'
                                      : 'Upload Logo'}
                                </span>
                              </>
                            )}
                          </button>

                          {(company.logo || currentOrganization?.imageUrl) && (
                            <button
                              onClick={handleRemoveLogo}
                              disabled={isUploadingLogo || !currentOrganization}
                              className="text-red-600 hover:text-red-800 px-3 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                            >
                              Remove
                            </button>
                          )}
                        </div>

                        <p className="text-xs text-gray-500">
                          Recommended: 512x512px, PNG or JPG, max 10MB
                        </p>
                      </div>

                      {isSupportSession && (
                        <p className="text-sm text-gray-600">
                          Company logo changes are unavailable during support because they update
                          the provider account.
                        </p>
                      )}
                      {/* Hidden File Input */}
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        className="hidden"
                      />
                    </div>
                  </div>

                  {isEditingCompany && (
                    <div className="flex justify-end space-x-3 pt-4">
                      <button
                        onClick={() => setIsEditingCompany(false)}
                        className="px-4 py-2 text-gray-600 hover:text-gray-800"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveCompany}
                        disabled={isSavingCompany}
                        className="bg-strategy-600 text-white px-4 py-2 rounded-lg hover:bg-strategy-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
                      >
                        {isSavingCompany ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            <span>Saving...</span>
                          </>
                        ) : (
                          <>
                            <Save className="h-4 w-4" />
                            <span>Save Changes</span>
                          </>
                        )}
                      </button>
                    </div>
                  )}

                  {/* Delete Company Section - Only visible to owners */}
                  {company?.role === 'owner' && (
                    <div className="border-t pt-6 mt-6">
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <div className="flex items-start">
                          <AlertTriangle className="h-5 w-5 text-red-400 mt-0.5 mr-3" />
                          <div className="flex-1">
                            <h4 className="text-sm font-medium text-red-800 mb-1">Danger Zone</h4>
                            <p className="text-sm text-red-700 mb-3">
                              Once you delete this company, there is no going back. This action will
                              permanently remove all company data, team members, strategies, and
                              projects.
                            </p>
                            <button
                              onClick={() => setShowDeleteModal(true)}
                              disabled={isDeletingCompany}
                              className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center space-x-2 text-sm"
                            >
                              <Trash2 className="h-4 w-4" />
                              <span>Delete Company</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === 'team' && (
            <motion.div
              key="team"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-6"
            >
              {/* Team Management */}
              <div className="bg-white rounded-lg shadow p-4 sm:p-6">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
                  <h2 className="text-lg sm:text-xl font-semibold text-gray-900 flex items-center">
                    <Users className="h-5 w-5 sm:h-6 sm:w-6 mr-2 text-strategy-600 flex-shrink-0" />
                    Team Management
                  </h2>
                  <div className="flex flex-wrap items-center gap-2 sm:space-x-3 sm:gap-0">
                    <button
                      onClick={() => setShowInviteModal(true)}
                      className="flex-1 min-w-[140px] sm:flex-none bg-strategy-600 text-white px-4 py-2.5 rounded-lg hover:bg-strategy-700 transition-colors flex items-center justify-center gap-2 text-sm font-medium"
                    >
                      <UserPlus className="h-4 w-4 flex-shrink-0" />
                      <span>Invite Member</span>
                    </button>
                    <button
                      onClick={handleSyncInvitations}
                      disabled={isSyncing}
                      className="px-3 py-2.5 sm:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-1.5 text-sm sm:text-xs"
                      title="Sync with Clerk and cleanup hanging invitations"
                    >
                      {isSyncing ? (
                        <>
                          <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin flex-shrink-0" />
                          <span>Syncing...</span>
                        </>
                      ) : (
                        <>
                          <Settings className="h-3.5 w-3.5 sm:h-3 sm:w-3 flex-shrink-0" />
                          <span>Sync</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {isLoadingMembers ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="w-6 h-6 border-2 border-strategy-600 border-t-transparent rounded-full animate-spin" />
                    <span className="ml-2 text-sm text-gray-500">Loading team...</span>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {teamMembers.length === 0 ? (
                      <p className="text-gray-500 text-center py-8">No team members found</p>
                    ) : (
                      <>
                        {/* Active Team Members */}
                        <div className="space-y-3">
                          <h3 className="text-base sm:text-lg font-medium text-gray-900">
                            Active Members
                          </h3>
                          {teamMembers
                            .filter((member) => member.status === 'active')
                            .map((member) => (
                              <div
                                key={member.id}
                                className="border border-gray-200 rounded-lg p-4"
                              >
                                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                                  <div className="flex items-center gap-3 min-w-0">
                                    <div className="h-10 w-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                                      {member.photo ? (
                                        <img
                                          src={member.photo}
                                          alt={member.name}
                                          className="h-10 w-10 rounded-full object-cover"
                                        />
                                      ) : (
                                        <span className="text-sm font-medium text-gray-600">
                                          {member.name.charAt(0).toUpperCase()}
                                        </span>
                                      )}
                                    </div>
                                    <div className="min-w-0">
                                      <div className="flex flex-wrap items-center gap-2">
                                        <p className="font-medium text-gray-900 truncate">
                                          {member.name}
                                        </p>
                                        {member.role === 'owner' && (
                                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-purple-100 text-purple-800 shrink-0">
                                            <Crown className="h-3 w-3 mr-1" />
                                            Owner
                                          </span>
                                        )}
                                      </div>
                                      <p className="text-sm text-gray-500 truncate">
                                        {member.email}
                                      </p>
                                    </div>
                                  </div>

                                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3 border-t border-gray-100 pt-3 sm:border-0 sm:pt-0">
                                    <div className="flex items-center gap-2">
                                      {getRoleIcon(member.role)}
                                      <span
                                        className={`px-2 py-1 text-xs rounded-full ${getRoleBadgeColor(member.role)}`}
                                      >
                                        {member.role}
                                      </span>
                                    </div>

                                    {member.role !== 'owner' && !member.adviserPracticeId ? (
                                      <div className="flex items-center gap-2">
                                        <select
                                          value={member.role}
                                          onChange={(e) =>
                                            handleUpdateRole(
                                              member.membershipId!,
                                              e.target.value as any
                                            )
                                          }
                                          disabled={processingMemberId === member.membershipId}
                                          className="flex-1 sm:flex-none text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-strategy-500 focus:border-transparent min-w-0"
                                        >
                                          <option value="admin">Admin</option>
                                          <option value="member">Member</option>
                                          <option value="viewer">Viewer</option>
                                        </select>
                                        <button
                                          onClick={() =>
                                            handleRemoveMember(member.membershipId!, member.name)
                                          }
                                          disabled={processingMemberId === member.membershipId}
                                          className="p-2 text-red-600 hover:text-red-800 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50 flex-shrink-0"
                                          title="Remove member"
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </button>
                                      </div>
                                    ) : (
                                      <p className="text-xs text-gray-500 sm:max-w-[200px]">
                                        Owner permissions cannot be modified
                                      </p>
                                    )}

                                    {processingMemberId === member.membershipId && (
                                      <div className="w-4 h-4 border-2 border-strategy-600 border-t-transparent rounded-full animate-spin flex-shrink-0" />
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                        </div>

                        {/* Pending Invitations */}
                        {invitations.filter((inv) => inv.status === 'pending').length > 0 && (
                          <div className="space-y-3">
                            <h3 className="text-base sm:text-lg font-medium text-gray-900">
                              Pending Invitations
                            </h3>
                            {invitations
                              .filter((inv) => inv.status === 'pending')
                              .map((invitation) => (
                                <div
                                  key={invitation.id}
                                  className="border border-yellow-200 rounded-lg p-4 bg-yellow-50"
                                >
                                  <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                                    <div className="flex items-start gap-3 min-w-0">
                                      <div className="h-10 w-10 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                                        <Mail className="h-5 w-5 text-yellow-600" />
                                      </div>
                                      <div className="min-w-0">
                                        <p className="font-medium text-gray-900 break-all">
                                          {invitation.invitedEmail}
                                        </p>
                                        <div className="flex flex-wrap items-center gap-2 mt-1">
                                          {getRoleIcon(invitation.role)}
                                          <span
                                            className={`px-2 py-0.5 text-xs rounded-full ${getRoleBadgeColor(invitation.role)}`}
                                          >
                                            {invitation.role}
                                          </span>
                                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-orange-100 text-orange-800">
                                            <Clock className="h-3 w-3 mr-1" />
                                            Invited
                                          </span>
                                        </div>
                                        <div className="text-xs text-gray-500 mt-2 space-y-0.5">
                                          <p>Created: {formatDate(invitation.createdAt)}</p>
                                          {invitation.inviterName && (
                                            <p>Invited by: {invitation.inviterName}</p>
                                          )}
                                          {invitation.expiresAt && (
                                            <p>Expires: {formatDate(invitation.expiresAt)}</p>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2 sm:flex-shrink-0">
                                      <button
                                        onClick={() =>
                                          handleUpdateInvitationStatus(invitation.id, 'revoked')
                                        }
                                        disabled={processingMemberId === invitation.id}
                                        className="text-sm px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 font-medium"
                                      >
                                        Revoke
                                      </button>
                                      {processingMemberId === invitation.id && (
                                        <div className="w-4 h-4 border-2 border-strategy-600 border-t-transparent rounded-full animate-spin flex-shrink-0" />
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}
